import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/useAuthStore';
import { getDefaultRouteForRole } from './roles';
import { UserRole } from './roles';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
}

/**
 * ProtectedRoute Component
 * Provides both authentication and authorization checks
 *
 * Features:
 * - Checks if user is authenticated
 * - Checks if user has permission to access the route
 * - Redirects unauthorized users to /login or their default route
 * - Uses role-based permissions from routes.ts config
 *
 * Usage:
 * <ProtectedRoute>
 *   <SomeProtectedPage />
 * </ProtectedRoute>
 *
 * or with specific roles:
 *
 * <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
 *   <AdminOnlyPage />
 * </ProtectedRoute>
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { isAuthenticated, user } = useAuthStore();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  console.log('🔐 ProtectedRoute: Checking access for path:', location.pathname);
  console.log('🔐 ProtectedRoute: Auth state:', { isAuthenticated, user: !!user });

  // Auth checking with Zustand store
  useEffect(() => {
    // Token expiry validation
    const tokenExpiry = localStorage.getItem('tokenExpiry');
    if (isAuthenticated && tokenExpiry) {
      const expiryTime = parseInt(tokenExpiry, 10);
      const now = Date.now();

      if (expiryTime <= now) {
        console.log('🔐 ProtectedRoute: Token expired - refresh needed');
      }
    }

    // Immediately stop checking once we have auth state from store
    setIsChecking(false);
  }, [isAuthenticated]);

  // STATE 1: Loading - Show loading state or shimmer
  if (isChecking) {
    console.log('🔐 ProtectedRoute: STATE 1 - Loading...');
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'var(--color-background, #fff)',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid rgba(89, 102, 168, 0.3)',
            borderTop: '3px solid #5966A8',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <div style={{ color: 'var(--color-text-primary, #333)' }}>Loading...</div>
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // STATE 2: Not Authenticated - Redirect to login
  if (!isAuthenticated || !user) {
    console.log('🔐 ProtectedRoute: STATE 2 - Not authenticated, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // STATE 3: Check Authorization (Role-based access control)
  const userRole = user.role as UserRole;

  console.log('🔐 ProtectedRoute: Current user role:', userRole);

  if (!userRole) {
    console.log('🔐 ProtectedRoute: No user role found, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has permission to access this route
  if (allowedRoles && allowedRoles.length > 0) {
    const hasPermission = allowedRoles.includes(userRole);
    console.log('🔐 ProtectedRoute: Using explicit allowedRoles, permission:', hasPermission);

    if (!hasPermission) {
      console.log('🚫 ProtectedRoute: Access denied! Redirecting to default route');
      const defaultRoute = getDefaultRouteForRole(userRole);
      return <Navigate to={defaultRoute} state={{ from: location }} replace />;
    }
  }

  // STATE 4: Authorized - Show protected content
  console.log('✅ ProtectedRoute: Access granted!');
  return <>{children}</>;
};

export default ProtectedRoute;
